import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Document, View } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { ChecklistTab } from '../data/checklistData';
import {
  ChatbeatPdfTemplate,
  ChatbeatPdfService,
  CoverTitle,
  SectionHeader,
  ChecklistItem,
  Paragraph,
  BulletList,
} from '../../../shared/services/pdf';

interface ChecklistPdfExportProps {
  projectName: string;
  activeTab: ChecklistTab;
  checkedTasks: Record<string, string>;
}

interface ChecklistDocumentProps {
  activeTab: ChecklistTab;
  checkedTasks: Record<string, string>;
  t: (key: string) => string;
}

const ChecklistDocument: React.FC<ChecklistDocumentProps> = ({ activeTab, checkedTasks, t }) => {
  const hasBullets = activeTab.introBulletKeys.length > 0;

  return (
    <Document>
      <ChatbeatPdfTemplate>
        <CoverTitle title={t(activeTab.introTitleKey)} subtitle={t(activeTab.introSubtitleKey)} />
        <Paragraph>{t(activeTab.introDescKey)}</Paragraph>

        {hasBullets && (
          <>
            <Paragraph>{t(activeTab.introHelperKey)}</Paragraph>
            <BulletList items={activeTab.introBulletKeys.map((k) => t(k))} />
          </>
        )}

        <Paragraph>{t(activeTab.introFooterKey)}</Paragraph>
        {activeTab.introFooter2Key && <Paragraph>{t(activeTab.introFooter2Key)}</Paragraph>}

        {activeTab.sections.map((section) => (
        <View key={section.id}>
          <SectionHeader
            title={t(section.titleKey)}
            subtitle={section.subtitleKey ? t(section.subtitleKey) : undefined}
          />
          {section.tasks.map((task, idx, arr) => {
            const tailSize = Math.min(3, arr.length);
            const tailStart = arr.length - tailSize;

            if (idx > tailStart && idx < arr.length) return null;

            if (idx === tailStart && tailSize > 1) {
              return (
                <View key={task.id} wrap={false}>
                  {arr.slice(tailStart).map((t2) => (
                    <ChecklistItem
                      key={t2.id}
                      title={t(t2.titleKey)}
                      description={t2.descriptionKey ? t(t2.descriptionKey) : undefined}
                      checked={t2.id in checkedTasks}
                    />
                  ))}
                </View>
              );
            }

            return (
              <ChecklistItem
                key={task.id}
                title={t(task.titleKey)}
                description={task.descriptionKey ? t(task.descriptionKey) : undefined}
                checked={task.id in checkedTasks}
              />
            );
          })}
        </View>
      ))}
      </ChatbeatPdfTemplate>
    </Document>
  );
};

const ChecklistPdfExport: React.FC<ChecklistPdfExportProps> = ({
  projectName,
  activeTab,
  checkedTasks,
}) => {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (isExporting) return;

    try {
      setIsExporting(true);
      const filename = `Checklist-${projectName}-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      const document = <ChecklistDocument activeTab={activeTab} checkedTasks={checkedTasks} t={t} />;
      await ChatbeatPdfService.generate(document, filename);
    } catch (error) {
      console.error('PDF export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button className="action-btn" onClick={handleExport} disabled={isExporting}>
      {isExporting ? (
        <span className="ico--loader ico--xs" />
      ) : (
        <span className="ico--download ico--xs" />
      )}
    </Button>
  );
};

export { ChecklistPdfExport };
export default ChecklistPdfExport;
